/* ---------- HUUG · theme.js ----------
   Lightweight, framework-free behaviors for the theme.
   Owns: drawer controller, header scroll detection, mega-menu hover, mobile-nav expand/collapse.
   Each component should ideally own its own JS, but these are cross-cutting.
---------- */

(function () {
  'use strict';

  // ---------- Drawer controller ----------

  // Closed drawers are `inert` (and visibility:hidden in base.css), so their
  // links and buttons drop out of the Tab order and the accessibility tree.
  // aria-hidden alone left ~30 invisible tab stops on every page. Open drawers
  // are modal: focus moves in, Tab wraps inside, Escape closes, and focus
  // returns to whatever opened it.
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const isVisible = (node) => !!(node.offsetWidth || node.offsetHeight || node.getClientRects().length);

  // Add to cart disables its button while the request runs, which drops focus
  // to <body> before the cart drawer opens. Remember the last control focused
  // outside any drawer so closing can still hand focus back to it.
  let lastFocusedOutside = null;
  document.addEventListener('focusin', (e) => {
    if (!e.target.closest('[data-drawer]')) lastFocusedOutside = e.target;
  });

  class Drawer {
    constructor(el) {
      this.el = el;
      this.name = el.dataset.drawer;
      this.isOpen = false;
      this.previouslyFocused = null;

      el.setAttribute('aria-modal', 'true');
      el.inert = true;

      el.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'Tab') this.trapTab(e);
      });
    }

    focusables() {
      return Array.from(this.el.querySelectorAll(FOCUSABLE)).filter(
        (node) => !node.classList.contains('drawer__backdrop') && isVisible(node)
      );
    }

    // Keep Tab / Shift+Tab inside the open drawer. Drawer content is often
    // re-rendered (cart lines, Quick Shop), so the list is read fresh each time.
    trapTab(e) {
      const items = this.focusables();
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!this.el.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Search lands in its input. Every other drawer lands on its close button,
    // which always exists; Quick Shop's body is still empty when it opens.
    focusInitial() {
      const items = this.focusables();
      const target =
        items.find((node) => node.matches('[data-search-input]')) ||
        items.find((node) => node.matches('button[data-drawer-close]')) ||
        items[0];
      if (target) target.focus();
    }

    setTriggersExpanded(expanded) {
      document.querySelectorAll(`[data-drawer-open="${this.name}"]`).forEach((btn) => {
        btn.setAttribute('aria-expanded', String(expanded));
      });
    }

    open() {
      if (this.isOpen) return;
      const active = document.activeElement;
      this.previouslyFocused =
        active && active !== document.body && !active.closest('[data-drawer]') ? active : lastFocusedOutside;
      this.el.inert = false;
      this.el.setAttribute('aria-hidden', 'false');
      this.el.classList.add('is-open');
      document.body.classList.add('drawer-open');
      this.isOpen = true;
      this.setTriggersExpanded(true);

      requestAnimationFrame(() => this.focusInitial());

      this.el.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.el.setAttribute('aria-hidden', 'true');
      this.el.classList.remove('is-open');
      this.el.inert = true;
      document.body.classList.remove('drawer-open');
      this.isOpen = false;
      this.setTriggersExpanded(false);

      const back = this.previouslyFocused;
      if (back && back.focus && document.contains(back)) {
        back.focus();
        // A trigger that is only shown on hover or focus-within (the collection
        // card's Quick shop pill) refuses focus while hidden. Fall back to its
        // card link, which reveals it again.
        if (document.activeElement !== back && back.parentElement) {
          const holder = back.parentElement.closest('a[href], [tabindex]:not([tabindex="-1"])');
          if (holder) holder.focus();
        }
      }

      this.el.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }
  }

  const drawers = new Map();
  document.querySelectorAll('[data-drawer]').forEach((el) => {
    drawers.set(el.dataset.drawer, new Drawer(el));
  });
  document.querySelectorAll('[data-drawer-open]').forEach((btn) => {
    if (drawers.has(btn.dataset.drawerOpen)) btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const drawer = drawers.get(btn.dataset.drawerOpen);
      if (drawer) drawer.open();
    });
  });
  window.huugDrawers = drawers;

  // ---------- Screen-reader announcements ----------
  // One visually hidden live region for status messages that have no visible
  // text of their own (add-to-cart results, filter counts). Polite by default.
  // Pass assertive for errors.
  const announcers = {};
  const huugAnnounce = (message, assertive) => {
    const key = assertive ? 'assertive' : 'polite';
    let region = announcers[key];
    if (!region) {
      region = document.createElement('div');
      region.className = 'visually-hidden';
      region.setAttribute('role', assertive ? 'alert' : 'status');
      region.setAttribute('aria-live', key);
      document.body.appendChild(region);
      announcers[key] = region;
    }
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 50);
  };
  window.huugAnnounce = huugAnnounce;

  // ---------- Alt-text filter tags ----------
  // Product image alts are "<description> | <tag>" (e.g. "... | black_rb"). The
  // tag drives gallery filtering, which reads the full alt from data-alt or
  // the product JSON. Shoppers and screen readers only get the description.
  // Liquid strips the tag where it renders alts; this catches the rest
  // (image_tag defaults, image-picker files, injected Quick Shop and cart markup).
  const stripAltTag = (img) => {
    const alt = img.getAttribute('alt');
    if (alt && alt.indexOf(' | ') !== -1) img.setAttribute('alt', alt.split(' | ')[0].trim());
  };
  document.querySelectorAll('img[alt*=" | "]').forEach(stripAltTag);
  new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'IMG') stripAltTag(node);
        else if (node.querySelectorAll) node.querySelectorAll('img[alt*=" | "]').forEach(stripAltTag);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // ---------- Header scroll detection ----------

  const header = document.querySelector('[data-site-header]');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  // ---------- Mega-menu hover (desktop) ----------

  const megaMenuItems = document.querySelectorAll('[data-mega-menu]');
  megaMenuItems.forEach((item) => {
    let timer;
    const open = () => {
      clearTimeout(timer);
      megaMenuItems.forEach((i) => i.classList.remove('is-open'));
      item.classList.add('is-open');
      const trigger = item.querySelector('[aria-haspopup]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };
    const closeWithDelay = () => {
      timer = setTimeout(() => {
        item.classList.remove('is-open');
        const trigger = item.querySelector('[aria-haspopup]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }, 120);
    };

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', closeWithDelay);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) closeWithDelay();
    });
  });

  // Close mega menu on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      megaMenuItems.forEach((i) => {
        i.classList.remove('is-open');
        const trigger = i.querySelector('[aria-haspopup]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // ---------- Mobile-nav submenu expand ----------

  document.querySelectorAll('[data-mobile-expand]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      // The toggle button may be the row itself (expand-only parents) or a
      // separate chevron next to a real link, so climb to the list item.
      const item = btn.closest('.mnav__item') || btn.parentElement;
      item.classList.toggle('is-expanded', !expanded);
    });
  });

  // ---------- Cart count refresh on cart updates ----------

  document.addEventListener('cart:updated', (e) => {
    const count = e.detail && e.detail.itemCount;
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    // The header button's accessible name carries the count too, and was
    // otherwise frozen at whatever it was when the page rendered.
    document.querySelectorAll('.site-header__cart[aria-label]').forEach((btn) => {
      btn.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  });

  // ---------- Quick Shop drawer ----------
  // Triggered by [data-product-quickshop="{handle}"] on any element. Lazy-loads
  // sections/quickshop-product.liquid via Section Rendering API and injects it
  // into the global drawer. After successful add-to-cart the drawer closes and
  // the cart drawer opens.

  const quickshopDrawer = drawers.get('quickshop');
  const quickshopBody = document.querySelector('[data-quickshop-body]');
  let quickshopFetchToken = 0;

  const moneyFormat = (cents) => {
    // Minimal formatter — Shopify's locale-aware formatter would need shop currency
    // settings injected; for now we mirror "$X" with no trailing zeros, matching
    // money_without_trailing_zeros for USD storefronts.
    const dollars = (cents / 100);
    const fixed = dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2);
    return '$' + fixed;
  };

  const setQuickshopLoading = (isLoading) => {
    if (!quickshopBody) return;
    const existing = quickshopBody.querySelector('[data-quickshop-loading]');
    if (isLoading && !existing) {
      const loader = document.createElement('div');
      loader.className = 'quickshop-drawer__loading';
      loader.setAttribute('data-quickshop-loading', '');
      loader.innerHTML = '<div class="quickshop-drawer__spinner" aria-hidden="true"></div>';
      quickshopBody.appendChild(loader);
    } else if (!isLoading && existing) {
      existing.remove();
    }
  };

  // Cache of fetched Quick Shop section HTML (inner, wrapper stripped), keyed by
  // product handle. Lets sibling silhouettes swap instantly without a spinner.
  const quickshopSectionCache = new Map();
  // Section id used for the currently-open drawer. Set by openQuickshop, read
  // by swapQuickshopStyle so style-switch swaps stay within the same drawer
  // variant (default vs. launch chip-styled).
  let quickshopSectionId = 'quickshop-product';

  const stripSectionWrapper = (html) => {
    // Strip Shopify's outer <div id="shopify-section-..."> wrapper but keep its
    // contents (which is what the section actually renders).
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    return tmp.firstElementChild ? tmp.firstElementChild.innerHTML : html;
  };

  const fetchQuickshopSection = (handle, sectionId) => {
    const sid = sectionId || 'quickshop-product';
    const cacheKey = sid + ':' + handle;
    if (quickshopSectionCache.has(cacheKey)) return Promise.resolve(quickshopSectionCache.get(cacheKey));
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(sid), {
      headers: { 'Accept': 'text/html' },
    }).then((res) => {
      if (!res.ok) throw new Error('quickshop fetch failed: ' + res.status);
      return res.text();
    }).then((html) => {
      const inner = stripSectionWrapper(html);
      quickshopSectionCache.set(cacheKey, inner);
      return inner;
    });
  };

  // Warm the browser image cache with a section's hero image so a style swap
  // shows its photo instantly. DOMParser doesn't fetch resources, so we pick the
  // hero src out of the (already-cached) HTML and preload just that one image.
  const preloadHeroFromInner = (inner) => {
    try {
      const doc = new DOMParser().parseFromString(inner, 'text/html');
      const hero = doc.querySelector('[data-quickshop-hero]') || doc.querySelector('.quickshop-product__hero');
      const src = hero && hero.getAttribute('src');
      if (src) { const im = new Image(); im.src = src; }
    } catch (e) { /* noop */ }
  };

  // Warm the cache with sibling silhouettes (HTML + hero image) so switching
  // styles is instant.
  const prefetchSiblingStyles = () => {
    if (!quickshopBody) return;
    quickshopBody.querySelectorAll('.pdp-style-switcher__option[data-handle]').forEach((el) => {
      const h = el.getAttribute('data-handle');
      if (h && !quickshopSectionCache.has(quickshopSectionId + ':' + h)) fetchQuickshopSection(h, quickshopSectionId).then(preloadHeroFromInner).catch(() => {});
    });
  };

  // Inject section HTML, re-execute its inline scripts (innerHTML doesn't run
  // them), wire product interactivity, then warm sibling styles.
  const renderQuickshopSection = (inner, preColor) => {
    quickshopBody.innerHTML = inner;
    quickshopBody.querySelectorAll('script:not([type="application/json"])').forEach((oldScript) => {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
    wireQuickshopProduct(quickshopBody, preColor);
    prefetchSiblingStyles();
  };

  const openQuickshop = async (handle, preColor, sectionId) => {
    if (!quickshopDrawer || !quickshopBody || !handle) return;
    // Remember which drawer variant this open used so style-switch swaps
    // stay within the same variant.
    quickshopSectionId = sectionId || 'quickshop-product';
    const token = ++quickshopFetchToken;
    quickshopBody.style.opacity = '';
    quickshopDrawer.open();

    // Warm cache hit → render immediately, no spinner.
    const cached = quickshopSectionCache.get(quickshopSectionId + ':' + handle);
    if (cached) { renderQuickshopSection(cached, preColor); return; }

    quickshopBody.innerHTML = '';
    setQuickshopLoading(true);
    try {
      const inner = await fetchQuickshopSection(handle, quickshopSectionId);
      if (token !== quickshopFetchToken) return; // a newer fetch superseded this one
      renderQuickshopSection(inner, preColor);
    } catch (err) {
      console.error(err);
      quickshopBody.innerHTML = '<div class="quickshop-drawer__error">Couldn’t load this product. Please try again.</div>';
    }
  };

  // Swap the drawer to a sibling silhouette in place — quick opacity crossfade,
  // no spinner. Instant when the cache is warm (prefetched on open).
  const swapQuickshopStyle = async (handle, preColor) => {
    if (!quickshopBody || !handle) return;
    const token = ++quickshopFetchToken;
    quickshopBody.style.transition = 'opacity 140ms ease';
    quickshopBody.style.opacity = '0.25';
    try {
      const inner = await fetchQuickshopSection(handle, quickshopSectionId);
      if (token !== quickshopFetchToken) return;
      renderQuickshopSection(inner, preColor);
      requestAnimationFrame(() => { quickshopBody.style.opacity = '1'; });
    } catch (err) {
      quickshopBody.style.opacity = '1';
      openQuickshop(handle, preColor, quickshopSectionId); // fall back to a full load
    }
  };

  const wireQuickshopProduct = (root, preColor) => {
    const productEl = root.querySelector('[data-quickshop-product]');
    if (!productEl) return;

    // ----- Variant matching -----
    const variantsScript = productEl.querySelector('[data-quickshop-variants]');
    let variants = [];
    try { variants = JSON.parse(variantsScript.textContent); } catch (e) { variants = []; }

    // Media manifest used for alt-tag-driven image filtering on color change.
    const mediaScript = productEl.querySelector('[data-quickshop-media]');
    let media = [];
    try { media = JSON.parse(mediaScript.textContent); } catch (e) { media = []; }

    const form = productEl.querySelector('[data-quickshop-form]');
    const variantInput = productEl.querySelector('[data-variant-id]');
    const priceEl = productEl.querySelector('[data-quickshop-price]');
    const addBtn = productEl.querySelector('[data-quickshop-add]');
    const addLabel = productEl.querySelector('[data-quickshop-add-label]');
    const addPrice = productEl.querySelector('[data-quickshop-add-price]');
    const installmentEl = productEl.querySelector('[data-quickshop-installment]');
    const heroImg = productEl.querySelector('[data-quickshop-hero]');
    const lowStockEl = productEl.querySelector('[data-quickshop-lowstock]');

    // Find the option position whose name is "color"/"colour", if any.
    let colorOptionIndex = -1;
    productEl.querySelectorAll('.variant-picker__group').forEach((group) => {
      const name = (group.dataset.optionName || '').toLowerCase();
      if (name === 'color' || name === 'colour') {
        colorOptionIndex = parseInt(group.dataset.optionPosition, 10) - 1;
      }
    });

    // Return the src of the first image whose alt contains the color (case-insensitive),
    // or null if no match. Editors set alt like "HUUG Tee — Black — Front".
    const findImageForColor = (color) => {
      if (!color) return null;
      const needle = color.toLowerCase();
      const hit = media.find((m) => (m.alt || '').toLowerCase().includes(needle));
      return hit ? hit.src : null;
    };

    const findVariant = (selectedOptions) => {
      return variants.find((v) => {
        return (
          (selectedOptions[0] === undefined || v.option1 === selectedOptions[0]) &&
          (selectedOptions[1] === undefined || v.option2 === selectedOptions[1]) &&
          (selectedOptions[2] === undefined || v.option3 === selectedOptions[2])
        );
      });
    };

    const readSelectedOptions = () => {
      const selected = [];
      productEl.querySelectorAll('.variant-picker__group').forEach((group) => {
        const pos = parseInt(group.dataset.optionPosition, 10);
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) selected[pos - 1] = checked.value;
      });
      return selected;
    };

    const updateForVariant = (variant) => {
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          if (addLabel) addLabel.textContent = 'Unavailable';
        }
        return;
      }
      if (variantInput) variantInput.value = variant.id;
      if (priceEl) {
        const curEl = priceEl.querySelector('[data-price-current]') || priceEl;
        const cmpEl = priceEl.querySelector('[data-price-compare]');
        const rootEl = priceEl.querySelector('[data-price]') || priceEl;
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        curEl.textContent = moneyFormat(variant.price);
        if (cmpEl) {
          if (onSale) { cmpEl.textContent = moneyFormat(variant.compare_at_price); cmpEl.hidden = false; }
          else { cmpEl.textContent = ''; cmpEl.hidden = true; }
        }
        rootEl.classList.toggle('price--on-sale', !!onSale);
      }
      if (addPrice) addPrice.textContent = moneyFormat(variant.price);
      if (installmentEl) installmentEl.textContent = moneyFormat(Math.round(variant.price / 4));
      if (addBtn) {
        addBtn.disabled = !variant.available;
        if (addLabel) {
          if (variant.available) {
            addLabel.innerHTML = 'Add to cart <span class="quickshop-form__add-sep" aria-hidden="true">·</span> <span data-quickshop-add-price>' + moneyFormat(variant.price) + '</span>';
          } else {
            addLabel.textContent = 'Sold out';
          }
        }
      }
      // Filter the gallery to images whose alt contains the selected color
      // (matches PDP behavior — editors tag alt like "Daily Embrace — Stone —
      // Front"). If no images match, show every image as the fallback.
      const selected = readSelectedOptions();
      const selectedColor = colorOptionIndex >= 0 ? selected[colorOptionIndex] : null;
      filterGalleryByColor(selectedColor);

      // Low-stock line: "Only N left [in <color>]" when the selected variant is
      // inventory-tracked with 5 or fewer units remaining.
      if (lowStockEl) {
        const qty = variant.inventory_quantity;
        const tracked = !!variant.inventory_management;
        if (tracked && typeof qty === 'number' && qty > 0 && qty <= 5) {
          lowStockEl.innerHTML = 'Only <strong>' + qty + ' left</strong>' +
            (selectedColor ? ' in ' + selectedColor : '');
          lowStockEl.hidden = false;
        } else {
          lowStockEl.hidden = true;
        }
      }

      // Hero image (mobile thumb): always reflect the first visible image
      // after filtering. Falls back to the variant's featured_image when
      // no alt-tag match exists.
      if (heroImg) {
        const colorMatch = findImageForColor(selectedColor);
        const nextSrc = colorMatch || (variant.featured_image && variant.featured_image.src);
        if (nextSrc) {
          heroImg.src = nextSrc;
          heroImg.removeAttribute('srcset');
        }
      }
    };

    // Toggle visibility of every gallery img based on alt-tag color match.
    const galleryImgs = Array.from(productEl.querySelectorAll('.quickshop-product__gallery img'));
    const filterGalleryByColor = (color) => {
      if (!galleryImgs.length) return;
      const needle = (color || '').toLowerCase().trim();
      if (!needle) {
        galleryImgs.forEach((img) => { img.hidden = false; });
        return;
      }
      const matches = galleryImgs.filter((img) => (img.alt || '').toLowerCase().includes(needle));
      // No alt-tag matches → show all (graceful fallback so we don't blank out
      // the gallery for products whose editors haven't tagged alts yet).
      const visible = matches.length ? matches : galleryImgs;
      galleryImgs.forEach((img) => { img.hidden = !visible.includes(img); });
      rebuildLoop();
    };

    // ---- Infinite loop for the mobile image carousel ----
    // Mirrors the PDP gallery: clone the edge slides of the CURRENT visible set
    // so a swipe past either end lands seamlessly on the matching real slide.
    // Mobile only (desktop is a vertical column). Rebuilt on filter / breakpoint.
    const galleryEl = productEl.querySelector('.quickshop-product__gallery');
    const loopMq = window.matchMedia('(max-width: 767px)');
    const visibleSlides = () =>
      galleryEl
        ? Array.from(galleryEl.querySelectorAll('.quickshop-product__hero:not([data-clone])')).filter((s) => !s.hidden)
        : [];
    const neutralizeClone = (node) => {
      node.setAttribute('data-clone', '');
      node.setAttribute('aria-hidden', 'true');
      node.setAttribute('tabindex', '-1');
      node.removeAttribute('data-quickshop-hero');
      node.removeAttribute('data-lightbox-source');
      node.removeAttribute('data-lightbox-trigger');
      node.removeAttribute('id');
      return node;
    };
    const removeClones = () => {
      if (galleryEl) galleryEl.querySelectorAll('[data-clone]').forEach((c) => c.remove());
    };
    const rebuildLoop = () => {
      if (!galleryEl) return;
      removeClones();
      if (!loopMq.matches) { galleryEl.scrollLeft = 0; return; } // desktop = column, no loop
      const reals = visibleSlides();
      const n = reals.length;
      if (n < 2) return; // 0 or 1 image → nothing to loop
      const sw = reals[0].offsetWidth || galleryEl.clientWidth;
      if (!sw) return; // not laid out yet (drawer still opening) — ResizeObserver retries
      const k = Math.min(Math.max(1, Math.round(galleryEl.clientWidth / sw)), n);
      reals.slice(n - k).forEach((s, j) => {
        const c = neutralizeClone(s.cloneNode(true));
        c.style.order = String(-1000 + j);
        galleryEl.appendChild(c);
      });
      reals.slice(0, k).forEach((s, j) => {
        const c = neutralizeClone(s.cloneNode(true));
        c.style.order = String(1000 + j);
        galleryEl.appendChild(c);
      });
      galleryEl.scrollLeft = reals[0].offsetLeft; // rest on the first real slide
    };
    const normalizeLoop = () => {
      if (!galleryEl || !loopMq.matches) return;
      const reals = visibleSlides();
      const n = reals.length;
      if (n < 2) return;
      const sw = reals[0].offsetWidth;
      if (!sw) return;
      const start = reals[0].offsetLeft;
      const realW = n * sw;
      const x = galleryEl.scrollLeft;
      if (x < start - sw * 0.5) galleryEl.scrollLeft = x + realW;        // in prepend clones
      else if (x > start + realW - sw * 0.5) galleryEl.scrollLeft = x - realW; // in append clones
    };
    if (galleryEl) {
      let settleTimer = null;
      galleryEl.addEventListener('scroll', () => {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(normalizeLoop, 140);
      }, { passive: true });
      if ('onscrollend' in galleryEl) galleryEl.addEventListener('scrollend', normalizeLoop);
      // Fires once the drawer lays the gallery out (0→real width) and on any
      // breakpoint/resize; hiding slides doesn't change the container's own box,
      // so filter changes call rebuildLoop() directly instead.
      if ('ResizeObserver' in window) {
        let roTimer = null;
        new ResizeObserver(() => {
          if (roTimer) clearTimeout(roTimer);
          roTimer = setTimeout(rebuildLoop, 120);
        }).observe(galleryEl);
      }
    }

    // Apply the initial color filter so the gallery starts on the right
    // images (server-renders the full stack, JS narrows to the selected color).
    {
      const selected = readSelectedOptions();
      const initialColor = colorOptionIndex >= 0 ? selected[colorOptionIndex] : null;
      filterGalleryByColor(initialColor);
    }

    productEl.querySelectorAll('.variant-picker__group').forEach((group) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', () => {
          // toggle is-selected on labels within this group
          group.querySelectorAll('.variant-picker__option').forEach((opt) => {
            const opInput = opt.querySelector('input[type="radio"]');
            opt.classList.toggle('is-selected', opInput && opInput.checked);
          });
          // Update the "selected name" span(s) for this group.
          group.querySelectorAll('[data-selected-color], [data-selected-option]').forEach((el) => {
            el.textContent = input.value;
          });
          // Mirror the chosen color's swatch into the trigger dot (the desktop
          // PDP chip shows a color dot instead of the name — Figma 2510:14615).
          const chosenOpt = input.closest('.variant-picker__option');
          const srcSwatch = chosenOpt && chosenOpt.querySelector('.variant-picker__swatch');
          if (srcSwatch) {
            group.querySelectorAll('[data-selected-swatch] .variant-picker__selected-dot').forEach((dot) => {
              dot.style.backgroundColor = srcSwatch.style.backgroundColor;
              dot.style.backgroundImage = srcSwatch.style.backgroundImage;
              dot.style.backgroundSize = srcSwatch.style.backgroundSize;
              dot.style.backgroundPosition = srcSwatch.style.backgroundPosition;
            });
          }
          updateForVariant(findVariant(readSelectedOptions()));
        });
      });
    });

    // Initial sync so price / gallery / low-stock reflect the opening variant.
    updateForVariant(findVariant(readSelectedOptions()));

    // If the opener passed a color (e.g. the PDP "Make it a set" card mirrors
    // the PDP's currently-selected color), pre-select the matching color swatch
    // so the bundle opens on the same color. Dispatching change reuses the
    // wiring above (labels, swatch dot, gallery filter, variant). Falls back
    // silently to the server default when the bundle lacks that color.
    if (preColor) {
      const colorGroup = productEl.querySelector('.variant-picker__group--color');
      if (colorGroup) {
        const want = preColor.toLowerCase();
        const match = Array.from(colorGroup.querySelectorAll('input[type="radio"]'))
          .find((r) => (r.value || '').toLowerCase() === want);
        if (match && !match.checked) {
          match.checked = true;
          match.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // ----- Quantity stepper -----
    const stepper = productEl.querySelector('[data-quantity-stepper]');
    if (stepper) {
      const input = stepper.querySelector('[data-quantity-input]');
      const dec = stepper.querySelector('[data-quantity-decrement]');
      const inc = stepper.querySelector('[data-quantity-increment]');
      const minVal = parseInt(input.getAttribute('min'), 10) || 1;
      dec.addEventListener('click', () => {
        const v = parseInt(input.value, 10) || minVal;
        input.value = Math.max(minVal, v - 1);
      });
      inc.addEventListener('click', () => {
        const v = parseInt(input.value, 10) || minVal;
        input.value = v + 1;
      });
    }

    // ----- AJAX add-to-cart -----
    if (form) {
      form.addEventListener('submit', async (e) => {
        // Let Shop Pay (payment_button) submit normally — we only intercept the
        // standard Add to Cart submit triggered by [data-quickshop-add].
        const submitter = e.submitter;
        if (submitter && submitter.matches('.shopify-payment-button button, .shopify-payment-button [role="button"]')) {
          return;
        }
        e.preventDefault();
        if (!addBtn || addBtn.disabled) return;

        const originalLabel = addLabel ? addLabel.innerHTML : '';
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Adding…';

        try {
          const fd = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: fd,
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.description || 'Could not add to cart');
          }

          // Refresh the header count + cart drawer (body, footer, subtotal,
          // recommendations) through the shared helper so Quick Shop and the PDP
          // run one identical path — no duplicated refresh logic to drift.
          await refreshCartDrawer();

          // Close quickshop, open cart drawer.
          if (quickshopDrawer) quickshopDrawer.close();
          const cartDrawer = drawers.get('cart');
          if (cartDrawer) {
            // Slight delay so the close animation reads visually before cart slides in.
            setTimeout(() => cartDrawer.open(), 180);
          }
          huugAnnounce('Added to cart');
        } catch (err) {
          console.error(err);
          if (addLabel) addLabel.innerHTML = originalLabel;
          if (addBtn) addBtn.disabled = false;
          // Surface the error inline near the button (best-effort).
          let errEl = form.querySelector('[data-quickshop-error]');
          if (!errEl) {
            errEl = document.createElement('p');
            errEl.setAttribute('data-quickshop-error', '');
            errEl.setAttribute('role', 'alert');
            errEl.style.cssText = 'color: #b00020; font-family: var(--font-sans); font-size: 14px; margin: 4px 0 0;';
            form.appendChild(errEl);
          }
          errEl.textContent = err.message || 'Could not add to cart.';
        }
      });
    }
  };

  // Capture phase so we run before any in-button stopPropagation handlers.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-product-quickshop]');
    if (!trigger) return;
    e.preventDefault();
    const handle = trigger.getAttribute('data-product-quickshop');
    if (!handle) return;
    // A trigger may pass an explicit data-quickshop-color to preselect, else none.
    // (The PDP "Make it a set" cards no longer open Quick Shop — they're shoppable
    // inline; see snippets/pdp-set-upsell.liquid.)
    const preColor = trigger.getAttribute('data-quickshop-color') || null;
    // Optional per-trigger drawer variant (e.g. "launch-quickshop-product" from
    // the launch-hero mobile CTA). Defaults to the standard quickshop-product.
    const sectionId = trigger.getAttribute('data-quickshop-section') || null;
    openQuickshop(handle, preColor, sectionId);
  }, true);

  // Quick Shop: clicking a silhouette in the style switcher should swap the
  // product WITHIN the drawer (like changing color) instead of navigating to
  // its PDP. Carries the currently-selected color across to the new style.
  document.addEventListener('click', (e) => {
    const styleOpt = e.target.closest('[data-quickshop-body] .pdp-style-switcher__option');
    if (!styleOpt) return;
    e.preventDefault();
    e.stopPropagation();
    if (styleOpt.classList.contains('is-selected')) return; // already this style
    const handle = styleOpt.getAttribute('data-handle');
    if (!handle) return;
    const colorInput = quickshopBody && quickshopBody.querySelector(
      '[data-option-name="Color"] [data-variant-option]:checked,' +
      '[data-option-name="Colour"] [data-variant-option]:checked'
    );
    swapQuickshopStyle(handle, colorInput ? colorInput.value : null);
  }, true);

  /* ---------- PDP main product: AJAX add-to-cart -> cart drawer ----------
   * The main PDP form previously submitted natively (full reload to /cart).
   * Intercept it so it matches Quick Shop: add via /cart/add.js, refresh the
   * drawer body, and slide the cart drawer open. Delegated so it's resilient.
   * Dynamic checkout buttons (Shop Pay etc.) still submit normally.
   * Registered in the CAPTURE phase so we intercept the submit before any
   * descendant/bubble-phase handler can swallow it — guaranteeing the AJAX
   * path runs instead of a native full-page navigation to /cart.
   * -------------------------------------------------------------------- */
  document.addEventListener('submit', async (e) => {
    // Matches on EITHER the legacy `.pdp-hero__form` class or the newer
    // `data-pdp-form` attribute. The class is emitted by both
    // sections/featured-product.liquid and sections/launch-hero.liquid via
    // `{% form 'product', product, class: 'pdp-hero__form' %}`, so it is a
    // shared contract — renaming it in one section without touching this line
    // silently drops that section back to a native full-page POST to /cart with
    // no error, just a navigation. The attribute is the stable hook so a section
    // can rename its classes without depending on this file.
    const form = e.target.closest && e.target.closest('form.pdp-hero__form, form[data-pdp-form]');
    if (!form) return;
    const submitter = e.submitter;
    if (submitter && submitter.matches('.shopify-payment-button button, .shopify-payment-button [role="button"]')) return;
    e.preventDefault();
    const addBtn = form.querySelector('[data-pdp-atc]');
    if (!addBtn || addBtn.disabled) return;
    const labelEl = form.querySelector('[data-pdp-atc-label]');
    const original = labelEl ? labelEl.textContent : '';
    addBtn.disabled = true;
    if (labelEl) labelEl.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || 'Could not add to cart');
      }
      await refreshCartDrawer();
      const cartDrawer = drawers.get('cart');
      if (cartDrawer) cartDrawer.open();
      huugAnnounce('Added to cart');
    } catch (err) {
      console.error(err);
      // There's no visible error here yet, so screen-reader users at least
      // hear why nothing happened instead of silence.
      huugAnnounce(err.message || 'Couldn’t add to cart. Please try again.', true);
    } finally {
      if (labelEl) labelEl.textContent = original;
      addBtn.disabled = false;
    }
  }, true);

  /* ---------- Cart drawer interactions (qty / remove / promo) -------------
   * Delegated handlers so they survive Section Rendering API swaps.
   * Every mutation refreshes the drawer body via /?section_id=cart-drawer
   * AND dispatches `cart:updated` so the B3G1 progress component recalculates.
   * ---------------------------------------------------------------------- */
  // Cart-aware recommendations: replace the drawer's collection "You'll love
  // these" with products related to what's in the cart (Shopify Recommendations
  // API). Falls back to the server-rendered collection recs when there are none.
  const loadCartRecommendations = async (cart) => {
    const list = document.querySelector('[data-cart-upsell-list]');
    if (!list) return;
    if (!cart) cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json()).catch(() => null);
    const items = cart && cart.items;
    if (!items || !items.length) return;
    const productId = items[0].product_id;
    if (!productId) return;
    try {
      const res = await fetch('/recommendations/products?section_id=cart-recommendations&product_id=' + productId + '&limit=6&intent=related', { headers: { 'Accept': 'text/html' } });
      if (!res.ok) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = (await res.text()).trim();
      const rows = tmp.querySelectorAll('.cart-upsell-row');
      if (!rows.length) return; // keep the collection fallback
      list.innerHTML = '';
      rows.forEach((row) => list.appendChild(row));
    } catch (e) { /* keep the fallback */ }
  };

  const refreshCartDrawer = async () => {
    const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await cartRes.json().catch(() => null);
    if (cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count } }));
    }
    const html = await fetch('/?section_id=cart-drawer', { headers: { 'Accept': 'text/html' } })
      .then((r) => (r.ok ? r.text() : null))
      .catch(() => null);
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    const newBody = tmp.querySelector('[data-cart-drawer-body]');
    const currentBody = document.querySelector('[data-cart-drawer-body]');
    if (newBody && currentBody) currentBody.innerHTML = newBody.innerHTML;
    loadCartRecommendations(cart);
    const newFooter = tmp.querySelector('[data-cart-drawer-footer]');
    const currentFooter = document.querySelector('[data-cart-drawer-footer]');
    if (newFooter && currentFooter) {
      currentFooter.outerHTML = newFooter.outerHTML;
    } else if (newFooter && !currentFooter) {
      // Was empty, now populated — append footer to the panel.
      const panel = document.querySelector('.cart-drawer__panel');
      if (panel) panel.appendChild(newFooter);
    } else if (!newFooter && currentFooter) {
      currentFooter.remove();
    }
    // The swapped footer arrives with an empty pending-codes slot.
    if (cart && window.renderPendingCodes) window.renderPendingCodes(cart);
  };
  // Expose so self-contained snippets (e.g. the PDP "Make it a set" inline cards)
  // can add to cart via /cart/add.js and then reuse the exact same drawer refresh
  // (body + footer + cart-aware recs + cart:updated) instead of duplicating it.
  window.refreshCartDrawer = refreshCartDrawer;

  /* ---------- Cart drawer "Make it a set" cards (snippets/cart-set-upsell*.liquid) ----
   * Delegated at the document level, same reasoning as qty/remove/promo above: this
   * module can appear via an innerHTML swap (a bra just got added, refreshCartDrawer
   * re-renders the body) where an embedded <script> tag would never execute, so ALL
   * interactivity lives here instead. Reads/writes state directly off the DOM (current
   * selected swatch, the size <select>'s value) rather than a per-card JS instance —
   * there's no init step to re-run after a swap, so this just keeps working.
   * ------------------------------------------------------------------------------- */
  const cartSetVariants = (card) => {
    const el = card.querySelector('[data-cart-set-variants]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) { return []; }
  };
  const cartSetResolve = (card, color, size) => {
    const hasColor = card.getAttribute('data-color-pos') !== '0';
    const hasSize = card.getAttribute('data-size-pos') !== '0';
    const matches = cartSetVariants(card).filter((v) => {
      if (hasColor && color != null && v.color !== color) return false;
      if (hasSize && size != null && v.size !== size) return false;
      return true;
    });
    return matches.find((v) => v.available) || matches[0] || null;
  };
  const cartSetRender = (card) => {
    const selectedSwatch = card.querySelector('[data-cart-set-swatch].is-selected');
    const sizeSel = card.querySelector('[data-cart-set-size]');
    const color = selectedSwatch ? selectedSwatch.getAttribute('data-color') : null;
    const size = sizeSel ? sizeSel.value : null;
    const v = cartSetResolve(card, color, size);
    const ok = !!(v && v.available);

    const addBtn = card.querySelector('[data-cart-set-add]');
    if (addBtn) {
      addBtn.disabled = !ok;
      addBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
      addBtn.setAttribute('data-variant-id', v ? v.id : '');
    }
    card.classList.toggle('is-unavailable', !ok);

    const priceEl = card.querySelector('[data-cart-set-price]');
    if (priceEl && v) {
      const cur = priceEl.querySelector('[data-price-current]');
      const cmp = priceEl.querySelector('[data-price-compare]');
      if (cur) cur.textContent = v.set_price_m;
      // compare_m is the struck-through price: the regular price under the
      // $5 set discount, the real compare-at for a marked-down item (which
      // the discount skips), or empty for neither.
      const compare = v.compare_m !== undefined ? v.compare_m : v.price_m;
      if (cmp) { cmp.textContent = compare; cmp.hidden = !compare; }
    }

    if (sizeSel) {
      Array.prototype.slice.call(sizeSel.options).forEach((opt) => {
        if (!opt.value) return;
        const stillOk = cartSetVariants(card).some((vv) => (
          (card.getAttribute('data-color-pos') === '0' || vv.color === color)
          && vv.size === opt.value && vv.available
        ));
        opt.disabled = !stillOk;
      });
    }
  };

  document.addEventListener('click', (e) => {
    const swatch = e.target.closest && e.target.closest('[data-cart-set-swatch]');
    if (!swatch) return;
    const card = swatch.closest('[data-cart-set-card]');
    if (!card) return;
    card.querySelectorAll('[data-cart-set-swatch]').forEach((btn) => {
      const on = btn === swatch;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const imgEl = card.querySelector('[data-cart-set-image]');
    if (imgEl) {
      const rep = cartSetResolve(card, swatch.getAttribute('data-color'), null);
      if (rep && rep.img) {
        imgEl.src = rep.img;
        imgEl.removeAttribute('srcset');
      }
    }
    cartSetRender(card);
  });

  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('[data-cart-set-size]');
    if (!sel) return;
    const card = sel.closest('[data-cart-set-card]');
    if (card) cartSetRender(card);
  });

  /* One line item payload for a set card, with the Final Sale property when
     the card is flagged for it (snippets/cart-set-upsell-card.liquid). */
  const buildSetAddItem = (card, variantId) => {
    const item = { id: variantId, quantity: 1 };
    const fs = card && card.getAttribute('data-final-sale');
    if (fs) item.properties = { 'Final Sale': fs };
    return item;
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-cart-set-add]');
    if (!btn || btn.disabled) return;
    const card = btn.closest('[data-cart-set-card]');
    const variantId = btn.getAttribute('data-variant-id');
    const errEl = card && card.querySelector('[data-cart-set-error]');
    if (!variantId) return;
    btn.disabled = true;
    if (card) card.classList.add('is-adding');
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      // Carry the Final Sale property the product forms attach as a hidden
      // input. /cart/add.js adds bypass those forms, so without this a Final
      // Sale item added from "Make it a set" arrives at checkout — and in
      // every order email — with nothing marking it.
      body: JSON.stringify({ items: [buildSetAddItem(card, variantId)] }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => { throw new Error((d && d.description) || 'Could not add to cart'); });
        return res.json();
      })
      .then(() => refreshCartDrawer())
      .catch((err) => {
        if (errEl) { errEl.textContent = err.message || 'Could not add to cart.'; errEl.hidden = false; }
      })
      .then(() => {
        btn.disabled = false;
        if (card) card.classList.remove('is-adding');
      });
  });

  const postCartChange = (id, quantity) => fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({ id, quantity })
  });

  /* Self-healing: /cart/change.js keys a line by `variant_id:properties_hash`,
   * baked into data-cart-line-key when the drawer last rendered. Confirmed
   * live: that key can already be stale by the time a shopper clicks Remove —
   * a third-party script (analytics/ads pixels, a Storefront GraphQL "GetCart"
   * query) fires several requests in the moments right after /cart/add.js, and
   * the cart's actual line key had already drifted before the drawer's first
   * render, let alone by the time of a later click. A stale key isn't silent:
   * /cart/change.js 400s with "no valid id or line parameter" — but nothing
   * surfaces that to the shopper (console.error only), so the button visibly
   * does nothing. Reopening the drawer doesn't help either, since
   * refreshCartDrawer() re-renders from that same already-drifted state; only
   * a full page reload forced a fresh key, which is exactly the reported
   * symptom.
   *
   * So on a failed attempt, before giving up: re-resolve the line by variant
   * id (data-cart-line-variant-id, carried for exactly this) against a fresh
   * /cart.js, and retry once with whatever key the server has right now.
   * Variant id isn't a perfect identity — two lines can share a variant with
   * different properties — but it's stable where the properties-hash half of
   * the key isn't, and this store doesn't cart the same variant twice under
   * different properties in the flows that hit this path.
   *
   * Verified live against the exact drift this was written for: the stale-key
   * POST returns 400; re-fetching /cart.js, matching by variant_id, and
   * retrying with THAT key succeeds and brings item_count to 0. */
  // The drawer body is replaced via innerHTML after every change, which drops
  // focus to <body> and destroys the per-line live region. Both are restored
  // here: a live region kept on the panel itself (outside the swapped body)
  // announces the result, and focus goes back to the same control on the same
  // line, or to the close button if the line is gone.
  const announceCart = (message) => {
    const panel = document.querySelector('.cart-drawer__panel');
    if (!panel) return;
    let region = panel.querySelector('[data-cart-announcer]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'visually-hidden';
      region.setAttribute('data-cart-announcer', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      panel.appendChild(region);
    }
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 50);
  };

  const restoreCartFocus = (variantId, control) => {
    const line = variantId && document.querySelector(`[data-cart-line-variant-id="${variantId}"]`);
    const target =
      (line && control && line.querySelector(control)) ||
      document.querySelector('.cart-drawer [data-drawer-close].cart-drawer__close') ||
      document.querySelector('.cart-drawer [data-drawer-close]');
    if (target) target.focus();
  };

  const changeCartLine = async (lineEl, quantity, control) => {
    if (!lineEl) return;
    const key = lineEl.getAttribute('data-cart-line-key');
    if (!key) return;
    const variantIdForFocus = lineEl.getAttribute('data-cart-line-variant-id');
    const hadFocus = lineEl.contains(document.activeElement);
    lineEl.classList.add('is-updating');
    try {
      let res = await postCartChange(key, quantity);

      if (!res.ok) {
        const variantId = lineEl.getAttribute('data-cart-line-variant-id');
        if (!variantId) throw new Error('Cart update failed');
        const fresh = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
        const freshLine = fresh.items.find((i) => String(i.variant_id) === variantId);
        if (!freshLine) {
          // Already gone server-side (e.g. a duplicate click landed first) —
          // nothing left to do, not a failure, as long as we were removing.
          if (quantity !== 0) throw new Error('Cart update failed');
        } else {
          res = await postCartChange(freshLine.key, quantity);
          if (!res.ok) throw new Error('Cart update failed');
        }
      }

      await refreshCartDrawer();
      announceCart(quantity === 0 ? 'Item removed from cart' : `Quantity updated to ${quantity}`);
      if (hadFocus) restoreCartFocus(variantIdForFocus, quantity === 0 ? null : control);
    } catch (err) {
      console.error(err);
      lineEl.classList.remove('is-updating');
      announceCart('Couldn’t update your cart. Please try again.');
    }
  };

  // Qty steppers
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-qty-step]');
    if (!btn) return;
    e.preventDefault();
    const lineEl = btn.closest('[data-cart-line]');
    if (!lineEl) return;
    const valueEl = lineEl.querySelector('[data-cart-qty-value]');
    const current = valueEl ? parseInt(valueEl.textContent, 10) || 0 : 0;
    const step = parseInt(btn.getAttribute('data-cart-qty-step'), 10) || 0;
    const next = Math.max(0, current + step);
    changeCartLine(lineEl, next, `[data-cart-qty-step="${step}"]`);
  });

  // Remove
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();
    const lineEl = btn.closest('[data-cart-line]');
    changeCartLine(lineEl, 0);
  });

  /* ---------- Discount codes: keep them on the cart and say what they're doing ----------
   * Welcome emails link to /discount/{code}. Shopify sets the `discount_code`
   * cookie AND adds the code to the cart, but a cart can hold several codes
   * while the cookie holds one, and every /discount/ hit overwrites it. So
   * everything theme-side now adds codes through /cart/update.js, sending the
   * full list (existing + new) so nothing already on the cart is dropped.
   *
   * Codes that are on the cart but not applying (the welcome codes need a $99
   * subtotal; FREEHUUG needs 4 bras) render as a coupon "sticker" in
   * [data-cart-pending-codes] (snippets/cart-discount-summary.liquid) saying
   * what unlocks them. A code saved in the cookie but missing from the cart gets
   * an Apply button. Applied codes are server-rendered by that snippet.
   *
   * The welcome rules come from theme settings (Theme settings → Welcome offer,
   * output by snippets/welcome-offer.liquid): the storefront API can't read a
   * code's minimum, only whether it currently applies.
   * ------------------------------------------------------------------------- */
  const WELCOME = (() => {
    const el = document.getElementById('huug-welcome-offer');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  })();
  const B3G1_CODE = 'FREEHUUG';
  const sameCode = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
  const isWelcomeCode = (code) => !!(WELCOME && WELCOME.prefix && String(code).toLowerCase().indexOf(WELCOME.prefix.toLowerCase()) === 0);
  const formatCents = (cents) => '$' + (cents / 100).toFixed(2);
  const readCookieCode = () => {
    const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m || !m[1]) return '';
    try { return decodeURIComponent(m[1]).trim(); } catch (e) { return m[1].trim(); }
  };
  const clearCookieCode = () => { document.cookie = 'discount_code=; Max-Age=0; path=/'; };
  const fetchCart = () => fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
  const setCartCodes = (codes) => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') })
  }).then((r) => { if (!r.ok) throw new Error('Discount update failed'); return r.json(); });

  // Adds `code` alongside whatever is already on the cart. Resolves to the cart.
  const addCartCode = async (code) => {
    const cart = await fetchCart();
    const codes = (cart.discount_codes || []).map((d) => d.code);
    if (codes.some((c) => sameCode(c, code))) return cart;
    return setCartCodes(codes.concat(code));
  };
  const removeCartCode = async (code) => {
    const cart = await fetchCart();
    const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => !sameCode(c, code));
    // Clear the cookie too, or the load-time sync below would put it straight back.
    if (sameCode(readCookieCode(), code)) clearCookieCode();
    return setCartCodes(codes);
  };
  window.huugAddDiscountCode = addCartCode;

  // What to tell the shopper about a code that isn't taking money off.
  const pendingNote = (entry, cart) => {
    if (!entry.onCart) return 'Saved for you, not on your cart yet';
    if (isWelcomeCode(entry.code) && WELCOME.min > 0) {
      const short = Math.round(WELCOME.min * 100) - (cart.items_subtotal_price || 0);
      if (short > 0) return 'Add ' + formatCents(short) + ' more to unlock';
    }
    if (sameCode(entry.code, B3G1_CODE)) return 'Unlocks with 4 bras in your cart';
    return 'Not applying to this cart';
  };

  const buildSticker = (entry, cart) => {
    const el = document.createElement('div');
    el.className = 'cart-sticker';
    el.setAttribute('data-code', entry.code);
    const body = document.createElement('div');
    body.className = 'cart-sticker__body';
    const head = document.createElement('p');
    head.className = 'cart-sticker__head t-body-3';
    const codeEl = document.createElement('strong');
    codeEl.textContent = entry.code;
    head.appendChild(codeEl);
    if (isWelcomeCode(entry.code) && WELCOME.percent) head.appendChild(document.createTextNode(' · ' + WELCOME.percent + '% off'));
    const note = document.createElement('p');
    note.className = 'cart-sticker__note t-body-3';
    note.textContent = pendingNote(entry, cart);
    body.append(head, note);
    el.appendChild(body);
    const btn = document.createElement('button');
    btn.type = 'button';
    if (entry.onCart) {
      btn.className = 'cart-sticker__remove';
      btn.setAttribute('data-cart-sticker-remove', '');
      btn.setAttribute('aria-label', 'Remove code ' + entry.code);
      btn.textContent = '×';
    } else {
      btn.className = 'cart-sticker__apply t-cta-sm';
      btn.setAttribute('data-cart-sticker-apply', '');
      btn.textContent = 'Apply';
    }
    el.appendChild(btn);
    return el;
  };

  const setWelcomeBannerText = (banner, entry) => {
    const text = banner.querySelector('[data-welcome-banner-text]');
    if (!text) return;
    text.textContent = entry.applicable
      ? 'Your ' + WELCOME.percent + '% off is applied.'
      : 'Your ' + WELCOME.percent + '% off is saved' + (WELCOME.min > 0 ? ' for orders of $' + WELCOME.min + '+.' : ' to your cart.');
  };

  const renderPendingCodes = (cart) => {
    if (!cart) return;
    const slots = document.querySelectorAll('[data-cart-pending-codes]');
    const codes = cart.discount_codes || [];
    const entries = codes.filter((d) => !d.applicable).map((d) => ({ code: d.code, onCart: true }));
    const cookieCode = readCookieCode();
    if (cookieCode && !codes.some((d) => sameCode(d.code, cookieCode))) entries.push({ code: cookieCode, onCart: false });
    // Keep a still-showing banner truthful as the cart crosses the minimum.
    const banner = document.querySelector('[data-welcome-banner]');
    const welcomeEntry = codes.find((d) => isWelcomeCode(d.code));
    if (banner && !banner.hidden && welcomeEntry) setWelcomeBannerText(banner, welcomeEntry);
    slots.forEach((slot) => {
      slot.textContent = '';
      entries.forEach((entry) => slot.appendChild(buildSticker(entry, cart)));
      slot.hidden = entries.length === 0;
    });
  };
  window.renderPendingCodes = renderPendingCodes;

  // Sticker buttons + the promo field all funnel through here.
  const applyCode = async (code, control) => {
    const label = control && control.textContent;
    if (control) { control.disabled = true; control.textContent = 'Applying…'; }
    try {
      const cart = await addCartCode(code);
      const entry = (cart.discount_codes || []).find((d) => sameCode(d.code, code));
      await refreshCartDrawer();
      huugAnnounce(entry && entry.applicable
        ? code + ' applied'
        : code + ' added. ' + pendingNote({ code: code, onCart: true }, cart));
      return true;
    } catch (e) {
      huugAnnounce('Couldn’t apply that code. Please try again.', true);
      return false;
    } finally {
      if (control && control.isConnected) { control.disabled = false; control.textContent = label; }
    }
  };

  document.addEventListener('click', (e) => {
    const apply = e.target.closest('[data-cart-sticker-apply]');
    const remove = e.target.closest('[data-cart-sticker-remove]');
    if (!apply && !remove) return;
    e.preventDefault();
    const code = (apply || remove).closest('.cart-sticker').getAttribute('data-code');
    if (apply) { applyCode(code, apply); return; }
    remove.disabled = true;
    removeCartCode(code)
      .then(() => refreshCartDrawer())
      .then(() => huugAnnounce(code + ' removed'))
      .catch(() => { remove.disabled = false; });
  });

  // Promo code field (drawer + cart page)
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-cart-promo]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = (input && input.value || '').trim();
    if (!code) return;
    applyCode(code, form.querySelector('.cart-drawer__promo-apply'));
  });

  // The cart page re-renders itself on cart:updated and fires it once more
  // when the new markup is in, so this catches its fresh, empty slot.
  let pendingTimer = 0;
  document.addEventListener('cart:updated', () => {
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => { fetchCart().then(renderPendingCodes).catch(() => {}); }, 150);
  });

  // Landing banner (snippets/welcome-offer.liquid): confirms the email's code
  // came through before anything is in the cart. Dismissal is per code.
  const showWelcomeBanner = (cart) => {
    const banner = document.querySelector('[data-welcome-banner]');
    if (!banner || !WELCOME || !WELCOME.banner) return;
    const entry = (cart.discount_codes || []).find((d) => isWelcomeCode(d.code));
    if (!entry) return;
    const key = 'huug-welcome-banner-' + entry.code.toLowerCase();
    try { if (sessionStorage.getItem(key)) return; } catch (e) { /* storage blocked: show it */ }
    setWelcomeBannerText(banner, entry);
    const hide = () => {
      banner.hidden = true;
      try { sessionStorage.setItem(key, '1'); } catch (e) { /* fine */ }
    };
    banner.hidden = false;
    const close = banner.querySelector('[data-welcome-banner-close]');
    if (close) close.addEventListener('click', hide);
    // It overlays the top of the page (it lives in the fixed header), so it
    // doesn't stay; hovering or focusing it holds it open.
    let timer = setTimeout(hide, 12000);
    const hold = () => clearTimeout(timer);
    const release = () => { clearTimeout(timer); timer = setTimeout(hide, 4000); };
    banner.addEventListener('mouseenter', hold);
    banner.addEventListener('focusin', hold);
    banner.addEventListener('mouseleave', release);
    banner.addEventListener('focusout', release);
  };

  // On load: if the cookie holds a code the cart lost (or never got), put it
  // back, then draw stickers and the banner from the real cart.
  (async () => {
    try {
      let cart = await fetchCart();
      const cookieCode = readCookieCode();
      const onCart = (cart.discount_codes || []).some((d) => sameCode(d.code, cookieCode));
      if (cookieCode && !onCart) {
        cart = await addCartCode(cookieCode);
        if (cart.item_count > 0) await refreshCartDrawer();
      }
      renderPendingCodes(cart);
      showWelcomeBanner(cart);
    } catch (e) { /* codes still apply at checkout via the cookie */ }
  })();

  // Init EVERY product strip on the page. `new Swiper(".class")` only binds the
  // FIRST match, so pages with multiple strips (home bestsellers + PDP + why-mesh)
  // left the rest as static grids. Scope each pagination to its own slider too.
  document.querySelectorAll(".product-trip__slider").forEach(function (sliderEl) {
    var pager = sliderEl.querySelector(".swiper-pagination");
    new Swiper(sliderEl, {
      slidesPerView: "auto",
      // Swiper applies this as an inline margin-right per slide (not CSS
      // gap) — verified against the real bundled Swiper build. Previously
      // this shrank to 8px at 768px+, the opposite of the usual "tighter on
      // cramped mobile, roomier on desktop" convention, and read as barely
      // any gap at all on desktop between 325px-wide cards.
      spaceBetween: 16,
      // Trackpad / horizontal wheel scrolling. forceToAxis keeps vertical
      // wheel intent on the page (won't hijack page scroll); releaseOnEdges
      // hands scroll back to the page once you hit either end. freeMode gives
      // the 1:1 trackpad feel instead of snapping slide-by-slide.
      freeMode: { enabled: true, sticky: false },
      mousewheel: { forceToAxis: true, releaseOnEdges: true },
      pagination: pager
        ? { el: pager, type: "progressbar" }
        : false,
    });
  });
const sliderEl = document.querySelector('[data-feature-slider]');

if (sliderEl) {
  const swiperFeautred = new Swiper(sliderEl, {
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 0,
    speed: 800,
    initialSlide: 0,
    loop: false,
    slideToClickedSlide: true,
    navigation: {
      nextEl: '.feature-slider__next',
      prevEl: '.feature-slider__prev',
    },
    pagination: {
      el: '.feature-slider__pagination',
      clickable: true,
    },
  });
}


new Swiper('.daily-collection-slider', {
    slidesPerView: 1,
    spaceBetween: 0,
    // Trackpad / horizontal-wheel scrolling, axis-locked so vertical wheel
    // still scrolls the page and hands back at the edges.
    freeMode: { enabled: true, sticky: false },
    mousewheel: { forceToAxis: true, releaseOnEdges: true },
    breakpoints:{
      768:{
        slidesPerView:1.3
      },
      1024:{
        slidesPerView:2.19
      }
    },

    pagination:{
      el:'.daily-collection .swiper-pagination',
      clickable:true
    }
  });

  const closeOffe = document.querySelector('.hero-offer__close');
  if(closeOffe){
    closeOffe.addEventListener('click', function(){
      this.closest('.hero-offer').style.display = "none";
    })
  }

  const productEl = document.querySelector('.page-product');
  if(productEl){
    const stepper = productEl.querySelector('[data-quantity-stepper]');
    if (stepper) {
      const input = stepper.querySelector('[data-quantity-input]');
      const dec = stepper.querySelector('[data-quantity-decrement]');
      const inc = stepper.querySelector('[data-quantity-increment]');
      const minVal = parseInt(input.getAttribute('min'), 10) || 1;
      dec.addEventListener('click', () => {
        const v = parseInt(input.value, 10) || minVal;
        input.value = Math.max(minVal, v - 1);
      });
      inc.addEventListener('click', () => {
        const v = parseInt(input.value, 10) || minVal;
        input.value = v + 1;
      });
    }
  }

  // ---------- Media video play/pause (snippets/media-video.liquid) ----------
  // Delegated on document so it works for any number of instances, anywhere
  // on the page, including ones injected later by the theme editor — no
  // per-section re-binding needed.
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-media-video-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const wrap = btn.closest('.media-video');
    const video = wrap && wrap.querySelector('video');
    if (!video) return;
    if (video.paused) {
      video.play();
      wrap.classList.remove('is-paused');
      btn.setAttribute('aria-label', 'Pause video');
    } else {
      video.pause();
      wrap.classList.add('is-paused');
      btn.setAttribute('aria-label', 'Play video');
    }
  });

})();
